import { useSound } from '@/contexts/SoundContext';
import { logger } from '@/services/logging';
import type { Direction } from '@/types/game';
import { useCallback, useEffect } from 'react';

interface UseKeyboardControlsProps {
  onMove: (direction: Direction) => void;
  onRestart: () => void;
  onHome: () => void;
  onSettings: () => void;
  onShop: () => void;
  onStats: () => void;
  onPowerup: (slot: number) => void;
  onEscape: () => void;
  onEnter: () => void;
  onLevelSelect: () => void;
  onProfile?: () => void;
  onWorlds?: () => void;
  onUpgrades?: () => void;
  disabled?: boolean;
  isHomeScreen?: boolean;
}

export const useKeyboardControls = ({
  onMove,
  onRestart,
  onHome,
  onSettings,
  onShop,
  onStats,
  onPowerup,
  onEscape,
  onEnter,
  onLevelSelect,
  onProfile,
  onWorlds,
  onUpgrades,
  disabled = false,
  isHomeScreen = false,
}: UseKeyboardControlsProps) => {
  const { playButtonClick, playPowerup } = useSound();

  // Simple direct handler like legacy - no refs or complex dependencies
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    logger.debug('keyboard', `Key pressed: ${event.key}`, {
      code: event.code,
      disabled,
      isHomeScreen,
      modifiers: {
        ctrl: event.ctrlKey,
        shift: event.shiftKey,
        alt: event.altKey,
      },
    });

    // Prevent default behavior for game controls
    const gameKeys = [
      'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
      'KeyW', 'KeyA', 'KeyS', 'KeyD',
      'KeyR', 'KeyF', 'KeyH', 'KeyS', 'KeyO', 'KeyU', 'Tab',
      'Digit1', 'Digit2', 'Digit3', 'Digit4',
      'Escape', 'Enter', 'KeyL'
    ];

    if (gameKeys.includes(event.code)) {
      event.preventDefault();
    }

    // Keep Escape available for closing overlays/modals even when movement is disabled.
    if (disabled && event.code !== 'Escape') {
      return;
    }

    // Movement controls - direct calls like legacy
    // Support both event.key (for arrow keys) and event.code (for WASD) to match legacy behavior
    if (!isHomeScreen && !disabled) {
      // Check by key first (for arrow keys)
      switch (event.key) {
        case 'ArrowRight':
          logger.logAction('keyboard', 'Move Right (Arrow)');
          onMove('right');
          return;
        case 'ArrowLeft':
          logger.logAction('keyboard', 'Move Left (Arrow)');
          onMove('left');
          return;
        case 'ArrowDown':
          logger.logAction('keyboard', 'Move Down (Arrow)');
          onMove('down');
          return;
        case 'ArrowUp':
          logger.logAction('keyboard', 'Move Up (Arrow)');
          onMove('up');
          return;
      }
      
      // Check by code for WASD keys
      // switch (event.code) {
      //   case 'KeyD':
      //     logger.logAction('keyboard', 'Move Right (D)');
      //     onMove('right');
      //     return;
      //   case 'KeyA':
      //     logger.logAction('keyboard', 'Move Left (A)');
      //     onMove('left');
      //     return;
      //   case 'KeyS':
      //     logger.logAction('keyboard', 'Move Down (S)');
      //     onMove('down');
      //     return;
      //   case 'KeyW':
      //     logger.logAction('keyboard', 'Move Up (W)');
      //     onMove('up');
      //     return;
      // }
    }

    // Global shortcuts
    switch (event.code) {
      case 'KeyR':
        if (!isHomeScreen) {
          logger.logAction('keyboard', 'Restart Level');
          playButtonClick();
          onRestart();
        }
        break;
      case 'KeyH':
        if (!isHomeScreen) {
          playButtonClick();
          onHome();
        }
        break;
      case 'Escape':
        logger.logAction('keyboard', 'Escape Pressed');
        playButtonClick();
        onEscape();
        break;
      case 'KeyS':
        // Settings shortcut. (WASD movement is currently disabled in this repo.)
        playButtonClick();
        onSettings();
        break;
      case 'KeyO':
        playButtonClick();
        onShop();
        break;
      case 'KeyP':
        // Changed from KeyA to KeyP to avoid conflict with movement
        if (onProfile) {
          playButtonClick();
          onProfile();
        }
        break;
      case 'Tab':
        if (!isHomeScreen) {
          playButtonClick();
          onStats();
        }
        break;
      case 'Digit1':
        if (!isHomeScreen) {
          playPowerup();
          onPowerup(1);
        }
        break;
      case 'Digit2':
        if (!isHomeScreen) {
          playPowerup();
          onPowerup(2);
        }
        break;
      case 'Digit3':
        if (!isHomeScreen) {
          playPowerup();
          onPowerup(3);
        }
        break;
      case 'Digit4':
        if (!isHomeScreen) {
          playPowerup();
          onPowerup(4);
        }
        break;
      case 'Enter':
        if (isHomeScreen) {
          logger.logAction('keyboard', 'Enter - Start Game');
          playButtonClick();
          onEnter();
        }
        break;
      case 'KeyL':
        if (isHomeScreen) {
          playButtonClick();
          onLevelSelect();
        }
        break;
      case 'KeyW':
        if (isHomeScreen && onWorlds) {
          playButtonClick();
          onWorlds();
        }
        break;
      case 'KeyU':
        if (isHomeScreen && onUpgrades) {
          playButtonClick();
          onUpgrades();
        }
        break;
    }
  }, [disabled, isHomeScreen, onMove, onRestart, onHome, onEscape, onSettings, onShop, onStats, onPowerup, onEnter, onLevelSelect, onWorlds, onUpgrades, playButtonClick, playPowerup]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return null;
};
